import { create } from "zustand";
import { restoreDraft } from "../persistence/draft";
import { hydrateProjectsFromStorage } from "../persistence/projects";
import { createWorkspaceAutosaver } from "../persistence/workspace";
import {
  useChatSessionStore,
  type ChatSessionRecord,
} from "./chat-session-store";
import { useProjectLibraryStore } from "./project-library-store";
import { normalizeRgbaHex } from "../skin/color";
import {
  cloneSkinDocument,
  createBlankSkin,
  documentsEqual,
} from "../skin/document";
import { createDefaultSkin } from "../skin/defaultSkins";
import { fetchJavaPlayerSkin } from "../skin/javaPlayerSkin";
import { applySkinOperations } from "../skin/operations";
import { exportSkinPng, importSkinPng } from "../skin/png";
import type {
  DrawOrigin,
  RgbaHex,
  SkinDocument,
  SkinLayer,
  SkinModel,
  SkinOperation,
  SkinSize,
  SurfaceRef,
} from "../skin/types";

export type EditorMode = "part" | "part3d" | "uv";
export type EditorTool =
  | "pencil"
  | "eraser"
  | "eyedropper"
  | "fill"
  | "line"
  | "rectangle";
export type BrushSize = 1 | 2 | 4;
export type DraftStatus = "loading" | "saving" | "saved" | "error" | "paused";

const AUTOSAVE_PREF_KEY = "skinpilot.autosave.enabled";

export function loadAutosavePreference(): boolean {
  if (typeof window === "undefined" || !("localStorage" in window)) return true;
  try {
    const value = window.localStorage.getItem(AUTOSAVE_PREF_KEY);
    if (value === null) return true;
    return value === "1" || value === "true";
  } catch {
    return true;
  }
}

export function saveAutosavePreference(enabled: boolean): void {
  if (typeof window === "undefined" || !("localStorage" in window)) return;
  try {
    window.localStorage.setItem(AUTOSAVE_PREF_KEY, enabled ? "1" : "0");
  } catch {
    // Preference is best-effort.
  }
}

export interface HistoryEntry {
  document: SkinDocument;
  source: DrawOrigin | "system";
  label?: string;
  chatSessionId?: string;
  rollbackOfChatSessionId?: string;
}

export interface ChatSessionRollbackResult {
  affectedPixels: number;
  modelChanged: boolean;
  transactionCount: number;
}

type OriginArgument = DrawOrigin | { source: DrawOrigin };

export interface SkinStoreState {
  document: SkinDocument;
  selectedSurface: SurfaceRef;
  editorMode: EditorMode;
  tool: EditorTool;
  color: RgbaHex;
  recentColors: RgbaHex[];
  brushSize: BrushSize;
  activeLayer: SkinLayer;
  showInner: boolean;
  showOuter: boolean;
  mirror: boolean;
  zoom: number;
  pan: { x: number; y: number };
  isAiRunning: boolean;
  draftStatus: DraftStatus;
  /** When false, changes stay in memory until manual save / project switch flush. */
  autosaveEnabled: boolean;
  history: HistoryEntry[];
  future: HistoryEntry[];
  strokeBase: SkinDocument | null;
  aiTransactionBase: SkinDocument | null;
  aiTransactionSessionId: string | null;

  setAutosaveEnabled(enabled: boolean): void;
  setSelectedSurface(surface: SurfaceRef): void;
  setEditorMode(mode: EditorMode): void;
  setTool(tool: EditorTool): void;
  setColor(color: RgbaHex): void;
  setBrushSize(size: BrushSize): void;
  setActiveLayer(layer: SkinLayer): void;
  toggleLayer(layer: SkinLayer): void;
  setMirror(mirror: boolean): void;
  setZoom(zoom: number): void;
  setPan(pan: { x: number; y: number }): void;
  setIsAiRunning(running: boolean): void;
  applyOperations(
    operations: readonly SkinOperation[],
    origin?: OriginArgument,
  ): number;
  beginStroke(): void;
  commitStroke(): boolean;
  undo(): boolean;
  redo(): boolean;
  setModelType(model: SkinModel, origin?: OriginArgument): void;
  loadDocument(document: SkinDocument): void;
  newBlank(model?: SkinModel, size?: SkinSize): void;
  newDefault(model?: SkinModel, size?: SkinSize): void;
  importJavaPlayer(playerId: string): Promise<string>;
  importPng(blob: Blob, model?: SkinModel): Promise<void>;
  exportPng(): Promise<Blob>;
  hydrateDraft(): Promise<boolean>;
  beginAiTransaction(): void;
  commitAiTransaction(): boolean;
  rollbackAiTransaction(): boolean;
  undoAiTransaction(): boolean;
  beginAITransaction(): void;
  commitAITransaction(): boolean;
  rollbackAITransaction(): boolean;
  undoLastAI(): boolean;
  rollbackChatSessionChanges(sessionId: string): ChatSessionRollbackResult;
}

const MAX_HISTORY = 100;
const initialSurface: SurfaceRef = {
  part: "head",
  layer: "inner",
  face: "front",
};

export const useSkinStore = create<SkinStoreState>((set, get) => ({
  document: createDefaultSkin(),
  selectedSurface: initialSurface,
  editorMode: "part",
  tool: "pencil",
  color: "#3E9F65FF",
  recentColors: [
    "#3E9F65FF",
    "#E36F45FF",
    "#62B7EFFF",
    "#F2B88FFF",
    "#29333DFF",
  ],
  brushSize: 1,
  activeLayer: "inner",
  showInner: true,
  showOuter: true,
  mirror: false,
  zoom: 1,
  pan: { x: 0, y: 0 },
  isAiRunning: false,
  draftStatus: "loading",
  autosaveEnabled: loadAutosavePreference(),
  history: [],
  future: [],
  strokeBase: null,
  aiTransactionBase: null,
  aiTransactionSessionId: null,

  setAutosaveEnabled: (enabled) => {
    saveAutosavePreference(enabled);
    set({
      autosaveEnabled: enabled,
      draftStatus: enabled ? "saved" : "paused",
    });
  },
  setSelectedSurface: (surface) =>
    set({ selectedSurface: { ...surface }, activeLayer: surface.layer }),
  setEditorMode: (editorMode) => set({ editorMode }),
  setTool: (tool) => set({ tool }),
  setColor: (value) => {
    const color = normalizeRgbaHex(value);
    set((state) => ({
      color,
      recentColors: [
        color,
        ...state.recentColors.filter((candidate) => candidate !== color),
      ].slice(0, 8),
    }));
  },
  setBrushSize: (brushSize) => {
    if (![1, 2, 4].includes(brushSize))
      throw new RangeError(`笔刷大小仅支持 1、2、4，收到：${brushSize}`);
    set({ brushSize });
  },
  setActiveLayer: (activeLayer) =>
    set((state) => ({
      activeLayer,
      selectedSurface: { ...state.selectedSurface, layer: activeLayer },
    })),
  toggleLayer: (layer) =>
    set((state) =>
      layer === "inner"
        ? { showInner: !state.showInner }
        : { showOuter: !state.showOuter },
    ),
  setMirror: (mirror) => set({ mirror }),
  setZoom: (zoom) => {
    if (!Number.isFinite(zoom)) throw new RangeError("缩放值必须是有限数字。");
    set({ zoom: Math.min(24, Math.max(0.5, zoom)) });
  },
  setPan: (pan) => {
    if (!Number.isFinite(pan.x) || !Number.isFinite(pan.y))
      throw new RangeError("平移坐标必须是有限数字。");
    set({ pan: { ...pan } });
  },
  setIsAiRunning: (isAiRunning) => set({ isAiRunning }),

  applyOperations: (operations, originArgument = "manual") => {
    const origin = readOrigin(originArgument);
    const state = get();
    if (state.isAiRunning && origin === "manual")
      throw new Error("AI 正在绘制，手工落笔已暂停。");
    const result = applySkinOperations(state.document, operations);
    if (result.affectedPixels === 0) return 0;

    const shouldRecordImmediately =
      (origin === "manual" && !state.strokeBase) ||
      (origin === "ai" && !state.aiTransactionBase);
    set({
      document: result.document,
      history: shouldRecordImmediately
        ? pushHistory(
            state.history,
            snapshotEntry(
              state.document,
              origin,
              undefined,
              origin === "ai"
                ? { chatSessionId: activeChatSessionId() }
                : undefined,
            ),
          )
        : state.history,
      future: shouldRecordImmediately ? [] : state.future,
    });
    return result.affectedPixels;
  },

  beginStroke: () => {
    const state = get();
    if (state.isAiRunning) throw new Error("AI 正在绘制，手工落笔已暂停。");
    if (!state.strokeBase)
      set({ strokeBase: cloneSkinDocument(state.document) });
  },
  commitStroke: () => {
    const state = get();
    if (!state.strokeBase) return false;
    const changed = !documentsEqual(state.strokeBase, state.document);
    set({
      strokeBase: null,
      history: changed
        ? pushHistory(
            state.history,
            snapshotEntry(state.strokeBase, "manual", "笔划"),
          )
        : state.history,
      future: changed ? [] : state.future,
    });
    return changed;
  },

  undo: () => performUndo(get, set),
  redo: () => performRedo(get, set),

  setModelType: (model, originArgument = "manual") => {
    const state = get();
    if (state.document.model === model) return;
    const origin = readOrigin(originArgument);
    if (state.isAiRunning && origin === "manual")
      throw new Error("AI 正在绘制，无法切换体型。");
    const next = {
      ...cloneSkinDocument(state.document),
      model,
      revision: state.document.revision + 1,
    };
    const shouldRecordImmediately =
      (origin === "manual" && !state.strokeBase) ||
      (origin === "ai" && !state.aiTransactionBase);
    set({
      document: next,
      history: shouldRecordImmediately
        ? pushHistory(
            state.history,
            snapshotEntry(
              state.document,
              origin,
              "切换体型",
              origin === "ai"
                ? { chatSessionId: activeChatSessionId() }
                : undefined,
            ),
          )
        : state.history,
      future: shouldRecordImmediately ? [] : state.future,
    });
  },

  loadDocument: (document) => {
    if (get().isAiRunning)
      throw new Error("AI 正在绘制，请先停止后再替换当前皮肤。");
    set({
      document: cloneSkinDocument(document),
      history: [],
      future: [],
      strokeBase: null,
      aiTransactionBase: null,
      aiTransactionSessionId: null,
      isAiRunning: false,
    });
  },
  newBlank: (model = get().document.model, size = get().document.size) =>
    get().loadDocument(createBlankSkin(model, size)),
  newDefault: (model = get().document.model, size = get().document.size) =>
    get().loadDocument(createDefaultSkin(model, size)),
  importJavaPlayer: async (playerId) => {
    const initialDocument = get().document;
    const initialProjectId = useProjectLibraryStore.getState().activeProjectId;
    if (get().isAiRunning)
      throw new Error("AI 正在绘制，请先停止后再替换当前皮肤。");
    const skin = await fetchJavaPlayerSkin(playerId);
    if (
      get().document !== initialDocument ||
      useProjectLibraryStore.getState().activeProjectId !== initialProjectId
    ) {
      throw new Error(
        "查询期间当前皮肤或项目已发生变化，已保留你的编辑，请重新导入。",
      );
    }
    get().loadDocument(skin.document);
    return skin.username;
  },
  importPng: async (blob, model) =>
    get().loadDocument(await importSkinPng(blob, model)),
  exportPng: () => exportSkinPng(get().document),
  hydrateDraft: async () => {
    // Prefer the full workspace path; keep this for legacy unit tests.
    const initialDocument = get().document;
    const draft = await restoreDraft();
    if (!draft) return false;
    if (get().isAiRunning || get().document !== initialDocument) return false;
    get().loadDocument(draft.document);
    set({
      selectedSurface: draft.metadata.selectedSurface ?? initialSurface,
      activeLayer:
        draft.metadata.activeLayer ??
        draft.metadata.selectedSurface?.layer ??
        "inner",
      editorMode: draft.metadata.editorMode ?? "part",
    });
    return true;
  },

  beginAiTransaction: () => beginAiTransaction(get, set),
  commitAiTransaction: () => commitAiTransaction(get, set),
  rollbackAiTransaction: () => rollbackAiTransaction(get, set),
  undoAiTransaction: () => undoAiTransaction(get, set),
  beginAITransaction: () => beginAiTransaction(get, set),
  commitAITransaction: () => commitAiTransaction(get, set),
  rollbackAITransaction: () => rollbackAiTransaction(get, set),
  undoLastAI: () => undoAiTransaction(get, set),
  rollbackChatSessionChanges: (sessionId) =>
    rollbackChatSessionChanges(get, set, sessionId),
}));

type GetState = () => SkinStoreState;
type SetState = (
  partial:
    | Partial<SkinStoreState>
    | ((state: SkinStoreState) => Partial<SkinStoreState>),
) => void;

function beginAiTransaction(get: GetState, set: SetState): void {
  const state = get();
  if (state.aiTransactionBase) throw new Error("已有 AI 修改事务正在进行。");
  if (state.strokeBase) state.commitStroke();
  const current = get();
  set({
    aiTransactionBase: cloneSkinDocument(current.document),
    aiTransactionSessionId: activeChatSessionId(),
    isAiRunning: true,
  });
}

function commitAiTransaction(get: GetState, set: SetState): boolean {
  const state = get();
  if (!state.aiTransactionBase) {
    set({ aiTransactionSessionId: null, isAiRunning: false });
    return false;
  }
  const changed = !documentsEqual(state.aiTransactionBase, state.document);
  const chatSessionId = state.aiTransactionSessionId ?? activeChatSessionId();
  set({
    aiTransactionBase: null,
    aiTransactionSessionId: null,
    isAiRunning: false,
    history: changed
      ? pushHistory(
          state.history,
          snapshotEntry(state.aiTransactionBase, "ai", "AI 设计轮次", {
            chatSessionId,
          }),
        )
      : state.history,
    future: changed ? [] : state.future,
  });
  return changed;
}

function rollbackAiTransaction(get: GetState, set: SetState): boolean {
  const state = get();
  if (!state.aiTransactionBase) {
    set({ aiTransactionSessionId: null, isAiRunning: false });
    return false;
  }
  const changed = !documentsEqual(state.aiTransactionBase, state.document);
  set({
    document: changed
      ? restoreWithNewRevision(state.aiTransactionBase, state.document.revision)
      : state.document,
    aiTransactionBase: null,
    aiTransactionSessionId: null,
    isAiRunning: false,
  });
  return changed;
}

function undoAiTransaction(get: GetState, set: SetState): boolean {
  const state = get();
  if (state.aiTransactionBase) return rollbackAiTransaction(get, set);
  if (state.history.at(-1)?.source !== "ai") return false;
  return performUndo(get, set);
}

function performUndo(get: GetState, set: SetState): boolean {
  const state = get();
  if (state.isAiRunning || state.strokeBase || state.history.length === 0)
    return false;
  const previous = state.history.at(-1)!;
  set({
    document: restoreWithNewRevision(
      previous.document,
      state.document.revision,
    ),
    history: state.history.slice(0, -1),
    future: pushHistory(
      state.future,
      snapshotEntry(
        state.document,
        previous.source,
        previous.label,
        historyEntryMetadata(previous),
      ),
    ),
  });
  return true;
}

function performRedo(get: GetState, set: SetState): boolean {
  const state = get();
  if (state.isAiRunning || state.strokeBase || state.future.length === 0)
    return false;
  const next = state.future.at(-1)!;
  set({
    document: restoreWithNewRevision(next.document, state.document.revision),
    history: pushHistory(
      state.history,
      snapshotEntry(
        state.document,
        next.source,
        next.label,
        historyEntryMetadata(next),
      ),
    ),
    future: state.future.slice(0, -1),
  });
  return true;
}

function restoreWithNewRevision(
  snapshot: SkinDocument,
  currentRevision: number,
): SkinDocument {
  return {
    pixels: new Uint8ClampedArray(snapshot.pixels),
    model: snapshot.model,
    size: snapshot.size,
    revision: currentRevision + 1,
  };
}

function snapshotEntry(
  document: SkinDocument,
  source: HistoryEntry["source"],
  label?: string,
  metadata: Pick<
    HistoryEntry,
    "chatSessionId" | "rollbackOfChatSessionId"
  > = {},
): HistoryEntry {
  return {
    document: cloneSkinDocument(document),
    source,
    label,
    ...metadata,
  };
}

function pushHistory(
  history: HistoryEntry[],
  entry: HistoryEntry,
): HistoryEntry[] {
  return [...history, entry].slice(-MAX_HISTORY);
}

function readOrigin(origin: OriginArgument): DrawOrigin {
  return typeof origin === "string" ? origin : origin.source;
}

function activeChatSessionId(): string {
  return useChatSessionStore.getState().activeSessionId;
}

function historyEntryMetadata(
  entry: HistoryEntry,
): Pick<HistoryEntry, "chatSessionId" | "rollbackOfChatSessionId"> {
  return {
    chatSessionId: entry.chatSessionId,
    rollbackOfChatSessionId: entry.rollbackOfChatSessionId,
  };
}

export function summarizeChatSessionChanges(
  history: readonly HistoryEntry[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const entry of history) {
    if (entry.rollbackOfChatSessionId) {
      counts.set(entry.rollbackOfChatSessionId, 0);
    }
    if (entry.chatSessionId) {
      counts.set(
        entry.chatSessionId,
        (counts.get(entry.chatSessionId) ?? 0) + 1,
      );
    }
  }
  return counts;
}

function rollbackChatSessionChanges(
  get: GetState,
  set: SetState,
  requestedSessionId: string,
): ChatSessionRollbackResult {
  const sessionId = requestedSessionId.trim();
  if (!sessionId) throw new Error("会话 ID 无效。");
  const state = get();
  if (state.isAiRunning || state.aiTransactionBase) {
    throw new Error("AI 正在绘制，请先停止后再回退会话修改。");
  }
  if (state.strokeBase) {
    throw new Error("请先完成当前笔划，再回退会话修改。");
  }

  let startIndex = 0;
  for (let index = state.history.length - 1; index >= 0; index -= 1) {
    if (state.history[index]?.rollbackOfChatSessionId === sessionId) {
      startIndex = index + 1;
      break;
    }
  }

  let transactionCount = 0;
  for (let index = startIndex; index < state.history.length; index += 1) {
    if (state.history[index]?.chatSessionId === sessionId) {
      transactionCount += 1;
    }
  }
  if (transactionCount === 0) {
    throw new Error("此对话暂无可回退的皮肤修改。");
  }

  const baseline = state.history[0]?.document ?? state.document;
  const pixelEvents: ReplayEvent<number>[][] = Array.from(
    { length: baseline.pixels.length / 4 },
    () => [],
  );
  const modelEvents: ReplayEvent<SkinModel>[] = [];

  for (let index = 0; index < state.history.length; index += 1) {
    const entry = state.history[index];
    if (!entry) continue;
    const before = entry.document;
    const after = state.history[index + 1]?.document ?? state.document;

    if (entry.rollbackOfChatSessionId) {
      for (
        let pixelIndex = 0;
        pixelIndex < pixelEvents.length;
        pixelIndex += 1
      ) {
        const events = pixelEvents[pixelIndex]!;
        removeSessionEvents(events, entry.rollbackOfChatSessionId);
        const offset = pixelIndex * 4;
        const historicalColor = readPackedPixel(after.pixels, offset);
        if (
          resolveReplayValue(
            events,
            readPackedPixel(baseline.pixels, offset),
          ) !== historicalColor
        ) {
          events.push({ value: historicalColor });
        }
      }

      removeSessionEvents(modelEvents, entry.rollbackOfChatSessionId);
      if (resolveReplayValue(modelEvents, baseline.model) !== after.model) {
        modelEvents.push({ value: after.model });
      }
      continue;
    }

    for (let offset = 0; offset < before.pixels.length; offset += 4) {
      if (pixelsEqualAt(before.pixels, after.pixels, offset)) continue;
      pixelEvents[offset / 4]!.push({
        chatSessionId: entry.chatSessionId,
        value: readPackedPixel(after.pixels, offset),
      });
    }
    if (before.model !== after.model) {
      modelEvents.push({
        chatSessionId: entry.chatSessionId,
        value: after.model,
      });
    }
  }

  const workingPixels = new Uint8ClampedArray(baseline.pixels);
  for (let pixelIndex = 0; pixelIndex < pixelEvents.length; pixelIndex += 1) {
    const events = pixelEvents[pixelIndex]!;
    removeSessionEvents(events, sessionId);
    writePackedPixel(
      workingPixels,
      pixelIndex * 4,
      resolveReplayValue(
        events,
        readPackedPixel(baseline.pixels, pixelIndex * 4),
      ),
    );
  }
  removeSessionEvents(modelEvents, sessionId);
  const workingModel = resolveReplayValue(modelEvents, baseline.model);

  let affectedPixels = 0;
  for (let offset = 0; offset < workingPixels.length; offset += 4) {
    if (!pixelsEqualAt(workingPixels, state.document.pixels, offset)) {
      affectedPixels += 1;
    }
  }
  const modelChanged = workingModel !== state.document.model;
  const nextDocument =
    affectedPixels > 0 || modelChanged
      ? {
          pixels: workingPixels,
          model: workingModel,
          size: state.document.size,
          revision: state.document.revision + 1,
        }
      : state.document;

  set({
    document: nextDocument,
    history: pushHistory(
      state.history,
      snapshotEntry(state.document, "system", "回退对话修改", {
        rollbackOfChatSessionId: sessionId,
      }),
    ),
    future: [],
  });

  return { affectedPixels, modelChanged, transactionCount };
}

function pixelsEqualAt(
  left: Uint8ClampedArray,
  right: Uint8ClampedArray,
  offset: number,
): boolean {
  return (
    left[offset] === right[offset] &&
    left[offset + 1] === right[offset + 1] &&
    left[offset + 2] === right[offset + 2] &&
    left[offset + 3] === right[offset + 3]
  );
}

type ReplayEvent<T> = {
  chatSessionId?: string;
  value: T;
};

function removeSessionEvents<T>(
  events: ReplayEvent<T>[],
  sessionId: string,
): void {
  let nextIndex = 0;
  for (const event of events) {
    if (event.chatSessionId === sessionId) continue;
    events[nextIndex] = event;
    nextIndex += 1;
  }
  events.length = nextIndex;
}

function resolveReplayValue<T>(
  events: readonly ReplayEvent<T>[],
  baseline: T,
): T {
  return events.at(-1)?.value ?? baseline;
}

function readPackedPixel(pixels: Uint8ClampedArray, offset: number): number {
  return (
    ((pixels[offset]! << 24) |
      (pixels[offset + 1]! << 16) |
      (pixels[offset + 2]! << 8) |
      pixels[offset + 3]!) >>>
    0
  );
}

function writePackedPixel(
  pixels: Uint8ClampedArray,
  offset: number,
  color: number,
): void {
  pixels[offset] = (color >>> 24) & 0xff;
  pixels[offset + 1] = (color >>> 16) & 0xff;
  pixels[offset + 2] = (color >>> 8) & 0xff;
  pixels[offset + 3] = color & 0xff;
}

/**
 * Restore the full local workspace (skin, editor, undo, chat) and keep it
 * autosaved to IndexedDB until the user replaces or clears it.
 */
export async function initializeDraftPersistence(): Promise<() => void> {
  try {
    const result = await hydrateProjectsFromStorage();
    useProjectLibraryStore
      .getState()
      .hydrate(result.projects, result.active.id);
    const autosaveEnabled = useSkinStore.getState().autosaveEnabled;
    useSkinStore.setState({
      draftStatus: autosaveEnabled ? "saved" : "paused",
    });
    // Bind autosave loop; seed save only when enabled and brand new.
    return bindWorkspaceAutosave(600, autosaveEnabled && !result.restored);
  } catch (error) {
    useSkinStore.setState({ draftStatus: "error" });
    throw error;
  }
}

export function bindWorkspaceAutosave(
  delayMilliseconds = 600,
  saveInitial = false,
): () => void {
  const autosaver = createWorkspaceAutosaver(delayMilliseconds, {
    onSaved: () => {
      const enabled = useSkinStore.getState().autosaveEnabled;
      useSkinStore.setState({ draftStatus: enabled ? "saved" : "paused" });
    },
    onError: () => useSkinStore.setState({ draftStatus: "error" }),
  });

  const initialSkinState = useSkinStore.getState();
  const initialChatState = useChatSessionStore.getState();
  let previousDocument = initialSkinState.document;
  let previousFingerprint = workspaceFingerprint(
    initialSkinState,
    initialChatState.activeSessionId,
    initialChatState.sessions,
  );

  const maybeSchedule = () => {
    const state = useSkinStore.getState();
    if (!state.autosaveEnabled || state.isAiRunning) return;
    const chatState = useChatSessionStore.getState();
    const nextFingerprint = workspaceFingerprint(
      state,
      chatState.activeSessionId,
      chatState.sessions,
    );
    if (
      nextFingerprint === previousFingerprint &&
      state.document === previousDocument
    ) {
      return;
    }
    previousFingerprint = nextFingerprint;
    previousDocument = state.document;
    useSkinStore.setState({ draftStatus: "saving" });
    autosaver.schedule();
  };

  const unsubscribeSkin = useSkinStore.subscribe((state, previous) => {
    // Turning autosave on: snapshot current workspace once.
    if (state.autosaveEnabled && !previous.autosaveEnabled) {
      previousFingerprint = "";
      maybeSchedule();
      return;
    }
    // Turning autosave off: cancel pending write.
    if (!state.autosaveEnabled && previous.autosaveEnabled) {
      autosaver.cancel();
      useSkinStore.setState({ draftStatus: "paused" });
      return;
    }
    maybeSchedule();
  });
  const unsubscribeChat = useChatSessionStore.subscribe(() => {
    maybeSchedule();
  });

  if (saveInitial && useSkinStore.getState().autosaveEnabled) {
    useSkinStore.setState({ draftStatus: "saving" });
    autosaver.schedule();
  }

  return () => {
    unsubscribeSkin();
    unsubscribeChat();
    if (useSkinStore.getState().autosaveEnabled) {
      void autosaver.flush().catch(() => undefined);
    } else {
      autosaver.cancel();
    }
  };
}

/** Manually persist the active project (works even when autosave is off). */
export async function saveWorkspaceNow(): Promise<void> {
  const { persistCurrentWorkspace } = await import("../persistence/workspace");
  useSkinStore.setState({ draftStatus: "saving" });
  try {
    await persistCurrentWorkspace();
    useSkinStore.setState({
      draftStatus: useSkinStore.getState().autosaveEnabled ? "saved" : "paused",
    });
  } catch (error) {
    useSkinStore.setState({ draftStatus: "error" });
    throw error;
  }
}

/** @deprecated Use bindWorkspaceAutosave */
export const bindDraftAutosave = bindWorkspaceAutosave;

function workspaceFingerprint(
  state: SkinStoreState,
  activeChatSessionId: string,
  chatSessions: readonly ChatSessionRecord[],
): string {
  const { part, layer, face } = state.selectedSurface;
  return [
    state.document.revision,
    state.document.model,
    state.history.length,
    state.future.length,
    part,
    layer,
    face,
    state.editorMode,
    state.tool,
    state.color,
    state.brushSize,
    state.activeLayer,
    state.showInner,
    state.showOuter,
    state.mirror,
    state.zoom,
    state.pan.x,
    state.pan.y,
    activeChatSessionId,
    chatSessions.length,
    // Content signal without full serialization (avoids huge base64 payloads).
    chatSessions
      .map((session) => {
        const messageSignal = session.messages
          .map((message) => {
            const textSignal = message.parts
              .map((part) => {
                if (typeof part !== "object" || part === null) return "x";
                const entry = part as { type?: string; text?: string };
                if (entry.type === "text" && typeof entry.text === "string") {
                  return `t${entry.text.length}`;
                }
                return entry.type ?? "p";
              })
              .join(",");
            return `${message.id}:${message.role}:${textSignal}`;
          })
          .join("|");
        return `${session.id}:${session.title}:${session.updatedAt}:${messageSignal}`;
      })
      .join("|"),
  ].join("::");
}

export function resetSkinStoreForTests(
  document: SkinDocument = createBlankSkin(),
): void {
  useSkinStore.getState().loadDocument(document);
  useSkinStore.setState({
    autosaveEnabled: loadAutosavePreference(),
    draftStatus: "saved",
  });
}
